import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { Routes } from '~/domains/common/constants/routes';
import { useRoutePrefetch } from '~/common/hooks/useRoutePrefetch';
import {
  LeaguePageHeader,
  type LeaguePageHeaderItem,
} from '~/domains/common/components/AppShellHeader/components/LeaguePageHeader/LeaguePageHeader';
import Image from '~/domains/common/components/Image/Image';

import { useQuickPicksFilters } from '../../../hooks/useQuickPicksFilters';
import { IconMapByLeague } from '../IconMap';

export function QuickPicksLeaguePageHeader() {
  const router = useRouter();
  const filtersQuery = useQuickPicksFilters();

  const leagues = useMemo(
    () => (filtersQuery.data?.filters?.leagues ?? []).filter((league) => league.id !== 'popular'),
    [filtersQuery.data?.filters?.leagues]
  );

  const leagueById = useMemo(
    () => new Map(leagues.map((league) => [league.id, league])),
    [leagues]
  );

  const items = useMemo<LeaguePageHeaderItem[]>(
    () =>
      leagues.map((league) => ({
        id: league.id,
        label: league.display_name,
        iconUrl: league.icon_url || undefined,
        disabled: !league.active,
      })),
    [leagues]
  );

  const activeLeagueId = router.query.league as string | undefined;

  const leagueRoutes = useMemo(
    () =>
      items.filter((item) => !item.disabled).map((item) => Routes.quickPicksBoardLeague(item.id)),
    [items]
  );

  useRoutePrefetch(leagueRoutes);

  const handleSelectLeague = useCallback(
    (item: LeaguePageHeaderItem) => {
      const league = leagueById.get(item.id);
      if (!league?.active) {
        return;
      }

      router.push(Routes.quickPicksBoardLeague(item.id));
    },
    [leagueById, router]
  );

  const handleSearchClick = useCallback(() => {
    const query = { ...router.query };
    delete query.boardSearchReturn;
    router.push({ pathname: Routes.quickPicksBoardSearch(), query });
  }, [router]);

  const renderItemIcon = useCallback(
    (item: LeaguePageHeaderItem, size: number) => {
      const league = leagueById.get(item.id);

      if (league?.icon_url) {
        return <Image src={league.icon_url} alt={league.display_name} width={size} height={size} />;
      }

      return <IconMapByLeague leagueId={item.id} size={size} />;
    },
    [leagueById]
  );

  return (
    <LeaguePageHeader
      backHref={Routes.quickPicksBoard({})}
      items={items}
      activeItemId={activeLeagueId}
      onSelectItem={handleSelectLeague}
      onSecondaryAction={handleSearchClick}
      secondaryActionAriaLabel="Search"
      variant="quickPicks"
      isLoading={filtersQuery.isLoading}
      renderItemIcon={renderItemIcon}
    />
  );
}
